/**
 * Подписка на уведомления по дивергенциям.
 *
 * Та же причина, что у подписки на «Выгодные зоны PRO»: индикатор на
 * графике считает то, что открыто сейчас, а уведомление должно прийти
 * при закрытой вкладке — значит инструмент, таймфрейм и настройки
 * сервер обязан знать заранее и хранить у себя.
 *
 * Отличие одно, и оно из-за природы самого расхождения. У зоны событие
 * привязано к одному таймфрейму: человек смотрит часовой и ждёт
 * часового сигнала. Расхождение же — утверждение о двух вершинах, и на
 * каждом таймфрейме оно своё: четырёхчасовое расхождение не видно на
 * пятнадцатиминутном графике, а пятнадцатиминутное на четырёхчасовом не
 * существует. Поэтому подписка заводится на каждый таймфрейм отдельно,
 * а в карточке индикатора они выбираются рядом кнопок — как у зон.
 *
 * Хранится в профиле человека, поэтому разбирается с проверкой: профиль
 * живёт дольше кода, и запись прошлой версии не должна ронять соседние.
 */
import { isTimeframe } from '@specter/types';
export const DIVERGENCE_WATCH_DEFAULTS = {
    source: 'all',
    minScore: 40,
    sensitivity: 5,
    maxGap: 60,
    hidden: true,
    forming: false,
    /*
     * По умолчанию будят все и с того же порога, что рисуются.
     *
     * Так решил человек, которому этим пользоваться: сигнал на каждое
     * расхождение. Планки на месте — поднять шумный источник можно в любой
     * момент, и это точнее, чем выключать его целиком.
     */
    signal: { spot: 40, futures: 40, flow: 40, hidden: true },
};
/** Имя источника в ленте и в уведомлении. По-русски: его читает человек. */
export const DIVERGENCE_WATCH_NAME = 'Дивергенции';
/** Ключ подписки. Собирается в одном месте: его разбирают и терминал, и сервер. */
export function divergenceWatchId(instanceId, venue, symbol, timeframe) {
    return `${instanceId}:${venue}|${symbol}|${timeframe}`;
}
/**
 * Подписки из профиля.
 *
 * Непонятная запись пропускается молча: подписка, которой разбор не
 * понял, лучше не сработает, чем сработает наугад.
 */
export function divergenceWatchesFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
        const watch = asDivergenceWatch(entry);
        if (!watch || seen.has(watch.id))
            continue;
        seen.add(watch.id);
        out.push(watch);
    }
    return out;
}
export function asDivergenceWatch(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const raw = value;
    if (typeof raw.id !== 'string' || raw.id === '')
        return null;
    if (typeof raw.venue !== 'string' || raw.venue === '')
        return null;
    if (typeof raw.symbol !== 'string' || raw.symbol === '')
        return null;
    if (!isTimeframe(raw.timeframe))
        return null;
    return {
        id: raw.id,
        venue: raw.venue,
        symbol: raw.symbol,
        timeframe: raw.timeframe,
        options: optionsFrom(raw.options),
        createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
    };
}
/*
 * Настройки восстанавливаются по полю, а не целиком.
 *
 * Запись, сделанная прошлой версией, знает не все поля. Отвергнуть её
 * значило бы выключить человеку уведомления молча; взять как есть —
 * считать по неполным настройкам. Поэтому каждое поле проверяется
 * отдельно, а недостающее берётся из умолчаний индикатора.
 */
function optionsFrom(value) {
    if (typeof value !== 'object' || value === null)
        return DIVERGENCE_WATCH_DEFAULTS;
    const raw = value;
    return {
        source: sourceFrom(raw.source),
        minScore: clamp(raw.minScore, DIVERGENCE_WATCH_DEFAULTS.minScore, 0, 100),
        sensitivity: clamp(raw.sensitivity, DIVERGENCE_WATCH_DEFAULTS.sensitivity, 1, 20),
        maxGap: clamp(raw.maxGap, DIVERGENCE_WATCH_DEFAULTS.maxGap, 10, 300),
        hidden: typeof raw.hidden === 'boolean' ? raw.hidden : DIVERGENCE_WATCH_DEFAULTS.hidden,
        forming: typeof raw.forming === 'boolean' ? raw.forming : DIVERGENCE_WATCH_DEFAULTS.forming,
        signal: levelsFrom(raw.signal),
    };
}
function levelsFrom(value) {
    const base = DIVERGENCE_WATCH_DEFAULTS.signal;
    if (typeof value !== 'object' || value === null)
        return base;
    const raw = value;
    return {
        spot: clamp(raw.spot, base.spot, 0, 100),
        futures: clamp(raw.futures, base.futures, 0, 100),
        flow: clamp(raw.flow, base.flow, 0, 100),
        hidden: typeof raw.hidden === 'boolean' ? raw.hidden : base.hidden,
    };
}
function sourceFrom(value) {
    return value === 'spot' || value === 'futures' || value === 'flow' || value === 'all'
        ? value
        : DIVERGENCE_WATCH_DEFAULTS.source;
}
function clamp(value, fallback, min, max) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return fallback;
    return Math.min(max, Math.max(min, value));
}
//# sourceMappingURL=divergence-watch.js.map